import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import InlineAlert from "../ui/InlineAlert";

const PENDING_URL_KEY = "renderrepo.pendingReviewUrl";

function reviewStartError(err) {
  const code = err?.response?.data?.code;
  if (code === "REPOSITORY_NOT_CONNECTED") return "Repository not connected.";
  if (code === "PR_NOT_FOUND" || code === "PR_FORBIDDEN") return "That pull request is not public. Connect GitHub if it is private.";
  if (code === "PR_CLOSED") return "Pull request closed.";
  if (code === "REVIEW_CREDIT_REQUIRED") return "You need one Review Credit to review this pull request.";
  if (code === "PRIVATE_INSTALLATION_REQUIRED") return "Connect GitHub to review this private repository.";
  if (code === "INVALID_PR_URL") return err?.response?.data?.error || "Use a GitHub pull request URL.";
  return err?.response?.data?.error || "Review could not be started.";
}

export function pullRequestProblem(value) {
  const raw = String(value || "").trim();
  if (!raw) return "Paste a GitHub pull request URL.";
  if (raw.length > 512) return "That URL is too long.";
  const candidate = raw.split(/[?#]/)[0].replace(/\/+$/, "");
  if (!/^(https?:\/\/)?(www\.)?github\.com\/[^/]+\/[^/]+\/pull\/\d+$/i.test(candidate)) {
    return "Use a link like https://github.com/owner/repo/pull/123.";
  }
  return "";
}

function shortSha(value) {
  return String(value || "").slice(0, 7);
}

function safeGithubUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname === "github.com" && url.pathname.startsWith("/apps/");
  } catch {
    return false;
  }
}

export default function StartDecisionReview({
  mutateAllowed = true,
  embedded = false,
  quiet = false,
  hero = false,
  initialUrl = "",
  autoPreview = false,
  onStarted,
}) {
  const [pullRequestUrl, setPullRequestUrl] = useState(initialUrl);
  const [error, setError] = useState("");
  const [needsCredit, setNeedsCredit] = useState(false);
  const [needsGitHub, setNeedsGitHub] = useState(false);
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [pending, setPending] = useState(false);
  const [preview, setPreview] = useState(null);
  const previewStarted = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const request = api.get("/billing/review-credits");
    if (request && typeof request.then === "function") {
      request.then((response) => {
        if (!cancelled) setBillingEnabled(Boolean(response.data?.billingEnabled));
      }).catch(() => {});
    }
    try {
      const saved = sessionStorage.getItem(PENDING_URL_KEY);
      if (saved && !initialUrl) setPullRequestUrl(saved);
    } catch { /* ignore */ }
    if (autoPreview && initialUrl && !pullRequestProblem(initialUrl) && !previewStarted.current) {
      previewStarted.current = true;
      api.post("/workspace/decision-reviews/preview", { pullRequestUrl: initialUrl.trim() }).then((response) => {
        if (!cancelled) setPreview(response.data?.preview || null);
      }).catch((err) => {
        if (!cancelled) setError(reviewStartError(err));
      });
    }
    return () => {
      cancelled = true;
    };
  }, [autoPreview, initialUrl]);

  const Heading = embedded ? "h2" : "h1";

  function rememberUrl() {
    try { sessionStorage.setItem(PENDING_URL_KEY, pullRequestUrl.trim()); } catch { /* ignore */ }
  }

  async function connectGitHub() {
    rememberUrl();
    setError("");
    try {
      const response = await api.get("/github-app/install-url");
      const url = response.data?.installUrl;
      if (!safeGithubUrl(url)) throw new Error("unsafe");
      window.location.assign(url);
    } catch {
      setError("GitHub connection is not available right now.");
    }
  }

  async function submit(event) {
    event.preventDefault();
    const problem = pullRequestProblem(pullRequestUrl);
    if (problem || pending) {
      setError(problem);
      return;
    }
    setError("");
    setNeedsCredit(false);
    setNeedsGitHub(false);
    setPending(true);
    try {
      if (!preview) {
        const response = await api.post("/workspace/decision-reviews/preview", { pullRequestUrl: pullRequestUrl.trim() });
        setPreview(response.data?.preview || null);
        setPending(false);
        return;
      }
      const response = await api.post("/workspace/decision-reviews", { pullRequestUrl: pullRequestUrl.trim() });
      const review = response.data?.review;
      const existing = Boolean(preview.alreadyReviewed) || (response.status === 200 && review?.status !== "analyzing" && review?.status !== "queued");
      try { sessionStorage.removeItem(PENDING_URL_KEY); } catch { /* ignore */ }
      onStarted?.(review, existing);
    } catch (err) {
      setPending(false);
      const code = err?.response?.data?.code;
      const creditRequired = code === "REVIEW_CREDIT_REQUIRED";
      const githubRequired = code === "PRIVATE_INSTALLATION_REQUIRED" || code === "PR_NOT_FOUND" || code === "PR_FORBIDDEN";
      setNeedsCredit(creditRequired);
      setNeedsGitHub(githubRequired);
      if (creditRequired || githubRequired) rememberUrl();
      setError(reviewStartError(err));
    }
  }

  if (!mutateAllowed) {
    return <InlineAlert>Your role can view reviews. A member starts a review.</InlineAlert>;
  }

  const confirmLabel = preview?.alreadyReviewed ? "Open this review" : "Review this exact commit";

  return (
    <section className={`${quiet ? "rr-launch" : "rr-launch rr-narrow"}${hero ? " rr-review-hero" : ""}`} data-testid="decision-review-panel">
      {quiet ? null : (
        <header>
          {hero ? <span className="rr-eyebrow">Product-aware code review</span> : null}
          <Heading>Review a pull request</Heading>
          <p>
            {hero
              ? "Paste a pull request. RenderRepo reviews that exact commit against what the product is supposed to do."
              : "RenderRepo reviews the exact current commit."}
          </p>
        </header>
      )}
      {quiet ? <p className="rr-launch-note">RenderRepo reviews the exact current commit.</p> : null}
      {hero || quiet ? null : billingEnabled ? <p>This review uses 1 credit.</p> : <p>Pilot reviews are not charged.</p>}
      <form className="rr-url-row" onSubmit={submit}>
        <label htmlFor={embedded ? "decision-review-url-dialog" : "decision-review-url"}>
          <span className={hero ? "rr-sr" : undefined}>Pull request URL</span>
          <input
            id={embedded ? "decision-review-url-dialog" : "decision-review-url"}
            data-testid="decision-review-url"
            value={pullRequestUrl}
            autoFocus
            onChange={(event) => {
              setPullRequestUrl(event.target.value);
              setPreview(null);
              setError("");
              setNeedsGitHub(false);
            }}
            placeholder="https://github.com/owner/repo/pull/123"
            aria-invalid={Boolean(error)}
          />
        </label>
        <button type="submit" className="rr-button" data-testid="decision-review-resolve" disabled={pending || !pullRequestUrl.trim()}>
          {pending ? (preview ? "Starting review…" : "Checking pull request…") : preview ? confirmLabel : hero ? "Review this PR" : "Review"}
        </button>
      </form>
      {preview ? (
        <div className="rr-pr-preview" data-testid="decision-review-preview">
          <strong>{preview.title}</strong>
          <p>
            {preview.owner}/{preview.name} #{preview.pullNumber}
            {preview.authorLogin ? ` · ${preview.authorLogin}` : ""}
            {` · ${preview.baseRef} ← ${preview.headRef} · ${shortSha(preview.headSha)}`}
          </p>
          <p>
            {preview.alreadyReviewed
              ? "This exact commit already has a review. Opening it does not use another credit."
              : preview.repositoryConnected
                ? "Review this exact commit."
                : "This public repository will be added, then the exact commit will be reviewed."}
          </p>
        </div>
      ) : null}
      {error ? <InlineAlert tone="alert">{error}</InlineAlert> : null}
      {needsGitHub ? (
        <p className="rr-launch-follow">
          <button type="button" className="rr-button secondary" onClick={connectGitHub}>Connect GitHub</button>
          <span>The pull request link stays here when you come back. An in-app review does not require GitHub Checks.</span>
        </p>
      ) : null}
      {needsCredit ? <p><Link to="/settings#billing">Add review credits</Link></p> : null}
      {hero ? (
        <div className="rr-review-promises">
          <span>Exact commit</span>
          <span>Starts immediately</span>
          <span>{billingEnabled ? "1 review credit" : "Free during pilot"}</span>
        </div>
      ) : null}
    </section>
  );
}
